import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { F90N } from '../../services/p9209Api';
import { useP9209Mode } from './hooks/useP9209Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useP9209Record } from './hooks/useP9209Record';
import { useP9209Navigation } from './hooks/useP9209Navigation';
import { useP9209Permissions } from './hooks/useP9209Permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Approval from './components/Tab2Approval';
import ActionBar from './components/ActionBar';
import { getApprovalStatus, isApprovalLocked, isFieldDisabledFn } from './helpers';
import './P9209Form.css';

const P9209Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useP9209Permissions();
  const { mode, setMode, isEditing } = useP9209Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError,
  } = useP9209Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useP9209Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load P9209 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: F90N) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.f90n_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      if (isApprovalLocked(record)) {
        message.error('已審核的申請單不可修改');
        return;
      }
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    if (isApprovalLocked(record)) {
      message.error('已審核的申請單不可刪除');
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除留職停薪申請單 ${record.f90n_01}?`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能開發中');
  }, [record, isEditing]);

  // Computed Values
  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing, mode), [isEditing, mode]);
  const statusBadge = getApprovalStatus(record);

  return (
    <PermissionGuard module={Modules.P9209} action="query">
    <div className="p9209-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>人事管理</a>
              <span>/</span>
              <span className="current">留職停薪申請單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>留職停薪申請單</h1>
            <span className="badge badge-default">9209</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <div className="field-group">
            <label>單號</label>
            <input
              value={String(formData.f90n_01 || '')}
              disabled
              readOnly
              style={{ width: 120 }}
            />
          </div>
          <div className="field-group">
            <label>員工</label>
            <span style={{ fontSize: 13 }}>
              {String(formData.f90n_11 || '')} {names.employee ? `(${names.employee})` : ''}
            </span>
          </div>
          <div className="field-group">
            <label>人事主管</label>
            <span style={{ fontSize: 13 }}>
              {String(formData.f90n_57 || '')} {names.hrStaff ? `(${names.hrStaff})` : ''}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['留職停薪申請資料', '核准'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 留職停薪申請資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              names={names}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              getFieldError={getFieldError}
            />
          </div>

          {/* Tab 2: 核准 (all readonly) */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Approval
              formData={formData}
              names={names}
            />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default P9209Form;
